import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import moment from 'moment';
import { Button, Col, Divider, Modal, Row, Tooltip } from 'antd';
import { isNil } from 'lodash';
import cx from 'classnames';
import DOMPurify from 'dompurify';

import {
  NamedAttachmentModel,
  RenewStatusEnum,
  ResourceDownloadResponse,
  AssignmentTemplateDetailsResponse,
  AssignmentTeammateSelectModel,
  AssignmentTerminationModel,
  AssignmentTypeEnum,
} from 'api/backofficeApiClient';
import {
  AssignmentDetailsModel,
  getAnticipateScheduleLabel,
  getAssignmentPageIdLabel,
  getAssignmentStatusClassName,
  getAssignmentStatusLabel,
  getAssignmentTerminationStatusName,
  getAssignmentTimesheetApprovers,
  getAssignmentTypeLabel,
  getEmploymentCategoryLabel,
  getEmploymentTypeLabel,
  getInternalReportingRateLabel,
  getKronosStatusName,
  getPartnerProfitSplit,
  getPreScreeningBilledToLabel,
  getSplitTypeLabel,
  getTeammateFullNameLabel,
  getWorkFromHomeLabel,
} from 'utils/assignmentUtils';
import {
  AssignmentCategoryEnum,
  AssignmentContactPersonViewModel,
  AssignmentInfoAdminResponseModel,
  AssignmentStatusTypeEnum,
  AssignmentTerminationToKronosEnum,
  KronosInfoStatusEnum,
} from 'api/adminApiClient';
import { getFullName } from 'utils/personUtils';
import { getLocation } from 'utils/locationUtils';
import DownloadAssignment from '../DownloadAssignment';
import { areObjectValuesPresent } from 'utils/objectUtils';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import BasePageCard from 'components/wrappers/BasePageCard';
import BasePageLabel from 'components/wrappers/BasePageLabel';
import { getWorkerInfoFile } from 'redux/reducers/workerSlice';
import {
  AssignmentInfoTypes,
  AssignmentSections,
} from 'features/assignment/enums';
import BaseAlert from 'components/wrappers/BaseAlert/BaseAlert';
import RenewAssignment from 'features/assignment/RenewAssignment';
import { downloadFile, getFileNameWithoutExt } from 'utils/fileUtils';
import { isAdminSelector } from 'redux/selectors/authenticationSelectors';
import PrimaryContactInfoSection from 'components/PrimaryContactInfoSection';
import {
  deleteAssignmentDraft,
  deleteAssignmentTemplate,
  getAssignmentSupportingDocumentFile,
  getW9InfoFileForDraftAssignment,
  updateKronosStatus,
} from 'redux/reducers/assignmentSlice';
import { getContractorCompanyInfoFile } from 'redux/reducers/contractorCompanySlice';
import BaseLabel from 'components/wrappers/BaseLabel';
import { useAppSelector } from 'hooks/useAppSelector';
import { useAppDispatch } from 'hooks/useAppDispatch';
import FinancialInfoDetails from './FinancialInfoDetails';
import BillingContactInfoSection from 'components/BillingContactInfoSection';
import DownloadUKGFile from '../DownloadUKGFile';
import TerminateAssignmentModal from 'components/modals/TerminateAssignmentModal';
import AssignmentActionSection from './AssignmentActionSection';
import { BaseTextEditorContent } from 'components/wrappers/BaseTextEditor';
import TerminationInfoModal from 'components/modals/TerminationInfoModal';
import RenewStatusLabel from 'components/RenewStatusLabel';

import styles from '../AssignmentDetailsPage.module.less';

export enum AssignmentMode {
  Template = 1,
  Partner = 2,
}

type Props = {
  mode?: AssignmentMode;
  loadAssignmentDetails: () => Promise<void>;
  assignmentDetails: AssignmentDetailsModel | AssignmentTemplateDetailsResponse;
  assignmentTerminationDetails?: AssignmentTerminationModel;
  loadPrimaryAndTerminationData?: () => Promise<void>;
  isAssignmentTerminationDetailsInitialLoaded?: boolean;
};

export default function AssignmentsPrimaryInfoSection(props: Props) {
  const {
    assignmentDetails,
    loadAssignmentDetails,
    mode,
    assignmentTerminationDetails,
    loadPrimaryAndTerminationData,
    isAssignmentTerminationDetailsInitialLoaded,
  } = props;
  const isAdminRole = useAppSelector(isAdminSelector);
  const [isTerminateAssignmentModalOpen, setIsTerminateAssignmentModalOpen] =
    useState(false);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isTemplate = mode === AssignmentMode.Template;
  const isPartner = mode === AssignmentMode.Partner;
  const {
    assignmentKey,
    assignmentType,
    assignmentDocument,
    assignmentTeammates,
    customerInfo,
    assignmentStatus,
    memberKey,
    memberCompanyName,
    partnerInfo,
    assignmentCategory,
    assignmentId,
    parentAssignmentId,
    parentAssignmentKey,
    renewStatus,
    renewKind,
    creator,
    renewType,
  } = assignmentDetails;

  if (!assignmentDocument) return null;

  const {
    supervisorInfo,
    timesheetApproverInfo,
    timesheetBackupApproverInfo,
    timesheetApproverNotApplicable,
    workLocation,
    billingLocation,
    billingEmail,
    splitInfo,
    assignmentInfo,
    financialInfo,
    bestPrimaryContacts,
    bestBillingContacts,
    workerInfo,
    companyInfo,
    financialScheduler,
  } = assignmentDocument;
  const isPendingAssignments =
    assignmentStatus === AssignmentStatusTypeEnum.Request;

  const checkIsCanTerminateAssignment = () => {
    if (!isAssignmentTerminationDetailsInitialLoaded) return false;

    const terminationKronosSyncInfo = (
      assignmentDetails as AssignmentInfoAdminResponseModel
    )?.terminationKronosSyncInfo;

    const isTerminationKronosSyncSuccess =
      terminationKronosSyncInfo?.ukgTerminationStatus ===
      AssignmentTerminationToKronosEnum.Success;
    const isApplied = assignmentTerminationDetails?.isApplied;
    const isRenewRequestStatus =
      renewStatus === RenewStatusEnum.RenewRequest ||
      renewStatus === RenewStatusEnum.ParentRenewRequest;
    const isModeNil = !isNil(mode);

    if (
      isTerminationKronosSyncSuccess ||
      isApplied ||
      isRenewRequestStatus ||
      isModeNil
    )
      return false;

    const isActiveOrRequest =
      assignmentStatus === AssignmentStatusTypeEnum.Active ||
      assignmentStatus === AssignmentStatusTypeEnum.Request;
    const isCompletedAndDetailsExist =
      assignmentStatus === AssignmentStatusTypeEnum.Completed &&
      !isNil(assignmentTerminationDetails);

    return isActiveOrRequest || isCompletedAndDetailsExist;
  };

  const isCanTerminateAssignment = checkIsCanTerminateAssignment();

  // https://mybasepay.atlassian.net/browse/MBO-3354
  const shouldSetUndefined =
    Object.keys(financialInfo).length === 1 &&
    financialInfo.hasOwnProperty('mbpRate');
  const resultFinancialInfo = shouldSetUndefined ? undefined : financialInfo;

  const joinedTeammateSections = () => {
    return (assignmentTeammates || []).length
      ? (assignmentTeammates
          ?.map<React.ReactNode>((recruiter: AssignmentTeammateSelectModel) => (
            <span key={recruiter.userId}>
              {getTeammateFullNameLabel(recruiter)}

              {!isNil(recruiter?.rate) && !isNil(recruiter.rateType) && (
                <>
                  <span>: </span>
                  <span className="title">
                    {getInternalReportingRateLabel(recruiter)}
                  </span>
                </>
              )}
            </span>
          ))
          .reduce((previous, current) => [
            previous,
            ', ',
            current,
          ]) as React.ReactNode[])
      : [];
  };

  const terminationKronosSyncInfo = (
    assignmentDetails as AssignmentInfoAdminResponseModel
  ).terminationKronosSyncInfo;

  const kronosSyncInfo = (assignmentDetails as AssignmentInfoAdminResponseModel)
    .kronosSyncInfo;

  const isCorporateAssignment =
    assignmentCategory === AssignmentCategoryEnum.Corporate;

  const isDraftAssignment = assignmentStatus === AssignmentStatusTypeEnum.Draft;

  const isTerminationSyncInProgressOrSuccessful =
    terminationKronosSyncInfo?.ukgTerminationStatus ===
      AssignmentTerminationToKronosEnum.InProgress ||
    terminationKronosSyncInfo?.ukgTerminationStatus ===
      AssignmentTerminationToKronosEnum.Success ||
    terminationKronosSyncInfo?.ukgTerminationStatus ===
      AssignmentTerminationToKronosEnum.Fail;

  const timesheetApprovers =
    getAssignmentTimesheetApprovers(assignmentDocument);

  const handleChangeKronosStatus = async () => {
    if (!assignmentId) return;
    const result = await dispatch(updateKronosStatus(assignmentId));

    if (updateKronosStatus.fulfilled.match(result)) {
      loadAssignmentDetails();
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const getKronosStatusTooltip = () => {
    switch (kronosSyncInfo?.ukgStatus) {
      case KronosInfoStatusEnum.SyncedWithErrors:
        return (
          <div>
            <p>
              Some values may not be synced correctly. Please review this
              assignment in UKG.
            </p>
            <div className={styles.statusBtn}>
              <Button onClick={handleChangeKronosStatus} size="small">
                Mark as reprocessed
              </Button>
            </div>
          </div>
        );
      case KronosInfoStatusEnum.SyncFailed:
        return (
          <div>
            <p>
              Synchronization of import {kronosSyncInfo?.failImportNumber}{' '}
              failed. Please process this assignment in UKG manually.
            </p>
            <div className={styles.statusBtn}>
              <Button onClick={handleChangeKronosStatus} size="small">
                Mark as reprocessed
              </Button>
            </div>
          </div>
        );
      case KronosInfoStatusEnum.AwaitingStatus:
        return (
          <div>
            <p>
              The assignment is being synced. It typically takes ~ 5 minutes. If
              not synced successfully, please report as an issue.
            </p>
          </div>
        );
    }
  };

  const supervisorFullName = getFullName(
    supervisorInfo?.firstName,
    supervisorInfo?.lastName,
  );
  const approverFullName = getFullName(
    timesheetApproverInfo?.firstName,
    timesheetApproverInfo?.lastName,
  );
  const backupApproverFullName = getFullName(
    timesheetBackupApproverInfo?.firstName,
    timesheetBackupApproverInfo?.lastName,
  );
  const workerAddress = getLocation(
    workerInfo?.location?.country,
    workerInfo?.location?.address,
    workerInfo?.location?.state || workerInfo?.location?.internationalStateName,
    workerInfo?.location?.city || workerInfo?.location?.internationalCityName,
    workerInfo?.location?.zipCode,
  );
  const companyAddress = getLocation(
    companyInfo?.location?.country,
    companyInfo?.location?.address,
    companyInfo?.location?.state ||
      companyInfo?.location?.internationalStateName,
    companyInfo?.location?.city || companyInfo?.location?.internationalCityName,
    companyInfo?.location?.zipCode,
  );
  const workAddress = getLocation(
    workLocation?.country,
    workLocation?.address,
    workLocation?.state || workLocation?.internationalStateName,
    workLocation?.city || workLocation?.internationalCityName,
    workLocation?.zipCode,
  );
  const billingAddress = getLocation(
    billingLocation?.country,
    billingLocation?.address,
    billingLocation?.state || billingLocation?.internationalStateName,
    billingLocation?.city || billingLocation?.internationalCityName,
    billingLocation?.zipCode,
  );

  const isSupervisorInfoVisible =
    supervisorFullName ||
    supervisorInfo?.title ||
    supervisorInfo?.email ||
    supervisorInfo?.phoneNumber;

  const isTimesheetApproverVisible =
    approverFullName ||
    timesheetApproverInfo?.email ||
    timesheetApproverInfo?.phoneNumber ||
    timesheetApproverNotApplicable;

  const isTimesheetBackupApproverVisible =
    backupApproverFullName ||
    timesheetBackupApproverInfo?.email ||
    timesheetBackupApproverInfo?.phoneNumber;

  const isWorkerInfoSpecified = !!(
    areObjectValuesPresent(workerInfo?.workerInfo) || workerAddress
  );

  const isCompanyInfoSpecified = !!(
    areObjectValuesPresent(companyInfo?.representativeContact) || companyAddress
  );
  const isGeneralInfoVisible = !!(
    isWorkerInfoSpecified ||
    isCompanyInfoSpecified ||
    assignmentType ||
    memberCompanyName
  );

  const jobDescriptionContent = assignmentInfo?.jobDescription
    ? DOMPurify.sanitize(assignmentInfo.jobDescription)
    : undefined;

  const isPrimaryContactInfoVisible = !!bestPrimaryContacts?.length;
  const isBillingContactsInfoVisible = !!bestBillingContacts?.length;

  const isAssignmentInfoVisible = Object.values(assignmentInfo || {}).some(
    Boolean,
  );
  const isWorksiteInfoVisible = !!(
    workAddress ||
    billingAddress ||
    billingEmail
  );

  const pageLabel = getAssignmentPageIdLabel({
    assignmentStatus,
    renewStatus,
    isTemplate,
    isPartner,
    renewKind,
  });

  const downloadAssignmentFile = async (
    fileOptions: ResourceDownloadResponse,
  ) => {
    try {
      await downloadFile({
        downloadUrl: fileOptions.downloadUrl,
        fileName: fileOptions.fileName,
      });
    } catch (e: any) {
      BaseAlert.error(e.message);
    }
  };

  const handleDownloadW9FileForDraftAssignment = async () => {
    if (!assignmentId) return;

    const result = await dispatch(
      getW9InfoFileForDraftAssignment(assignmentId),
    );

    if (getW9InfoFileForDraftAssignment.fulfilled.match(result)) {
      await downloadAssignmentFile(result.payload);
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const handleDownloadWorkerFile = async () => {
    if (!workerInfo?.workerId && isDraftAssignment) {
      return handleDownloadW9FileForDraftAssignment();
    }

    if (!workerInfo?.workerId) return;

    const result = await dispatch(
      getWorkerInfoFile({
        workerId: workerInfo.workerId,
        isAdminRole: isAdminRole as boolean,
      }),
    );

    if (getWorkerInfoFile.fulfilled.match(result)) {
      await downloadAssignmentFile(result.payload);
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const handleDownloadContractorCompanyFile = async () => {
    if (!companyInfo?.companyId && isDraftAssignment) {
      return handleDownloadW9FileForDraftAssignment();
    }

    if (!companyInfo?.companyId) return;

    const result = await dispatch(
      getContractorCompanyInfoFile({
        companyId: companyInfo.companyId,
        isAdminRole: isAdminRole as boolean,
      }),
    );

    if (getContractorCompanyInfoFile.fulfilled.match(result)) {
      await downloadAssignmentFile(result.payload);
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const onRemoveTemplate = async () => {
    if (!assignmentId) return;

    const result = await dispatch(deleteAssignmentTemplate(assignmentId));

    if (deleteAssignmentTemplate.fulfilled.match(result)) {
      BaseAlert.success('Assignment template have been removed.');
      navigate(`/assignments?section=${AssignmentSections.Templates}`);
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const onRemoveAssignmentDraft = async () => {
    if (!assignmentId) return;

    const result = await dispatch(deleteAssignmentDraft(assignmentId));

    if (deleteAssignmentDraft.fulfilled.match(result)) {
      BaseAlert.success('Assignment draft have been removed.');
      navigate(`/assignments?section=${AssignmentSections.Drafts}`);
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const handleTemplateRemove = () => {
    return Modal.confirm({
      title: 'The template will be deleted. Are you sure?',
      icon: <span className="icon icon-attention-filled" />,
      async onOk() {
        await onRemoveTemplate();
      },
      onCancel() {},
    });
  };

  const handleAssignmentDraftRemove = () => {
    return Modal.confirm({
      title: 'The assignment draft will be permanently deleted. Are you sure?',
      icon: <span className="icon icon-attention-filled" />,
      async onOk() {
        await onRemoveAssignmentDraft();
      },
      onCancel() {},
    });
  };

  const handleTerminateAssignmentOpen = () => {
    setIsTerminateAssignmentModalOpen(true);
  };

  const handleTerminateAssignmentClose = () => {
    setIsTerminateAssignmentModalOpen(false);
  };

  const handleSupportingFileDownload = async (file: NamedAttachmentModel) => {
    const result = await dispatch(
      getAssignmentSupportingDocumentFile({
        fileKey: file.fileKey || '',
        fileName: file.fileName || '',
        isAdmin: isAdminRole,
      }),
    );

    if (getAssignmentSupportingDocumentFile.fulfilled.match(result)) {
      try {
        await downloadFile({
          downloadUrl: result.payload.downloadUrl,
          fileName: result.payload.fileName,
        });
      } catch (e: any) {
        BaseAlert.error(e.message);
      }
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  return (
    <>
      {isTerminateAssignmentModalOpen && !isNil(assignmentId) && (
        <TerminateAssignmentModal
          onClose={handleTerminateAssignmentClose}
          assignmentId={assignmentId}
          onSuccess={loadPrimaryAndTerminationData}
          assignmentTerminationDetails={assignmentTerminationDetails}
          isPendingAssignment={isPendingAssignments}
        />
      )}

      {pageLabel && <BasePageLabel label={pageLabel} creator={creator} />}

      <div className="page-box page-inner">
        <div className="page-inner-container">
          <div className="page-head">
            <div className="page-head__left">
              <div className="page-title page-head__title">
                {isTemplate
                  ? (assignmentDetails as AssignmentTemplateDetailsResponse)
                      ?.templateName
                  : assignmentKey}
              </div>

              <Row gutter={[10, 10]}>
                {!isTemplate && (
                  <Col>
                    <BaseLabel
                      status={getAssignmentStatusLabel(
                        assignmentStatus as AssignmentStatusTypeEnum,
                      )}
                      statusClassName={getAssignmentStatusClassName(
                        assignmentStatus as AssignmentStatusTypeEnum,
                      )}
                    />
                  </Col>
                )}
                {isTemplate && (
                  <Col>
                    <BaseLabel status="Template" />
                  </Col>
                )}

                <Col>
                  <div className={cx('page-head__label', styles.statusWrap)}>
                    {isAdminRole &&
                      (!isNil(kronosSyncInfo) ||
                        isTerminationSyncInProgressOrSuccessful) && (
                        <>
                          {!isNil(kronosSyncInfo) && (
                            <div className={styles.statusWrapFailed}>
                              <BaseLabel
                                status={getKronosStatusName(
                                  kronosSyncInfo?.ukgStatus,
                                )}
                              />
                              {kronosSyncInfo?.ukgStatus !==
                                KronosInfoStatusEnum.ReprocessedManually &&
                                kronosSyncInfo?.ukgStatus !==
                                  KronosInfoStatusEnum.Synced && (
                                  <Tooltip
                                    overlayClassName="light-tooltip"
                                    title={getKronosStatusTooltip}
                                    className={styles.statusWrapIcon}
                                    trigger="click"
                                    placement="topLeft"
                                    getPopupContainer={(triggerNode) =>
                                      triggerNode.parentNode as HTMLElement
                                    }
                                  >
                                    <i
                                      className={'icon icon-attention-filled'}
                                    />
                                  </Tooltip>
                                )}
                            </div>
                          )}

                          {isTerminationSyncInProgressOrSuccessful && (
                            <div>
                              <BaseLabel
                                status={getAssignmentTerminationStatusName(
                                  terminationKronosSyncInfo?.ukgTerminationStatus,
                                )}
                              />
                            </div>
                          )}
                        </>
                      )}

                    <div>
                      <RenewStatusLabel
                        renewStatus={renewStatus}
                        renewKind={renewKind}
                        renewType={renewType}
                      />
                    </div>
                  </div>
                </Col>
              </Row>
            </div>

            <div className="page-head__actions">
              {isCanTerminateAssignment &&
                !isNil(assignmentTerminationDetails) && (
                  <div>
                    <Button
                      onClick={handleTerminateAssignmentOpen}
                      className="with-icon"
                    >
                      <i className={'icon icon-edit'} />
                      Termination scheduled for{' '}
                      {moment(
                        assignmentTerminationDetails.terminationDate,
                      ).format('MM/DD/YYYY')}
                    </Button>
                  </div>
                )}

              {assignmentTerminationDetails?.isApplied && (
                <TerminationInfoModal
                  assignmentId={assignmentId}
                  assignmentTerminationDetails={assignmentTerminationDetails}
                  assignmentStatus={assignmentStatus}
                />
              )}

              <RenewAssignment assignmentDetails={assignmentDetails} />

              <AssignmentActionSection
                assignmentId={assignmentId as string}
                loadAssignmentDetails={loadAssignmentDetails}
                loadPrimaryAndTerminationData={loadPrimaryAndTerminationData}
                isTemplate={isTemplate}
                isPartnerMode={isPartner}
                assignmentDetails={assignmentDetails}
                assignmentTerminationDetails={assignmentTerminationDetails}
                isCanTerminateAssignment={isCanTerminateAssignment}
              />

              {isTemplate && (
                <div>
                  <Button
                    className="with-icon"
                    icon={<span className="icon icon-delete" />}
                    onClick={handleTemplateRemove}
                  >
                    Delete
                  </Button>
                </div>
              )}
              {/*On a template page we view two delete buttons, so we had to add the first check as well*/}
              {!isPartner && !isTemplate && isDraftAssignment && (
                <div>
                  <Button
                    className="with-icon"
                    icon={<span className="icon icon-delete" />}
                    onClick={handleAssignmentDraftRemove}
                  >
                    Delete
                  </Button>
                </div>
              )}

              <DownloadAssignment
                assignmentId={assignmentId}
                assignmentStatus={assignmentStatus}
                isPartner={isPartner}
              />

              {isAdminRole && !isNil(kronosSyncInfo) && (
                <DownloadUKGFile
                  assignmentId={assignmentId}
                  ukgStatus={kronosSyncInfo?.ukgStatus}
                />
              )}
            </div>
          </div>
          <Row gutter={[20, 20]}>
            {(isGeneralInfoVisible ||
              isWorksiteInfoVisible ||
              isAssignmentInfoVisible) && (
              <Col xs={24} md={12} className="d-flex">
                <Row gutter={[8, 8]}>
                  {isGeneralInfoVisible && (
                    <Col xs={24} className="d-flex">
                      <BasePageCard
                        title="General information"
                        icons={
                          <span className="icon icon-writing-tool-user"></span>
                        }
                        className="stretch-card"
                      >
                        <div className="content-wrapper">
                          <Row gutter={[8, 8]} className="titled-sheet">
                            {!!assignmentType && (
                              <Col xs={24} md={12}>
                                <div
                                  className="title"
                                  data-id="assignmentGeneralType"
                                >
                                  Assignment type:
                                </div>
                                <div>
                                  {getAssignmentTypeLabel(assignmentType)}
                                </div>
                              </Col>
                            )}

                            {!!parentAssignmentId && !!parentAssignmentKey && (
                              <Col xs={24} md={12} data-id="requestRelatedTo">
                                <div className="title">Related to:</div>

                                <Link
                                  className="card-link"
                                  to={`/assignment/${parentAssignmentKey}?section=${AssignmentInfoTypes.Primary}`}
                                >
                                  <span>{parentAssignmentKey}</span>
                                </Link>
                              </Col>
                            )}

                            {!!memberCompanyName && (
                              <Col
                                xs={24}
                                md={12}
                                data-id="assignmentGeneralMemberCompany"
                              >
                                <div className="title">Member company:</div>
                                {isAdminRole ? (
                                  <Link to={`/member/${memberKey}`}>
                                    {memberCompanyName}
                                  </Link>
                                ) : (
                                  <div>{memberCompanyName}</div>
                                )}
                              </Col>
                            )}
                            {(assignmentType ===
                              AssignmentTypeEnum.ICAssignment ||
                              assignmentType ===
                                AssignmentTypeEnum.SCAssignment) &&
                              companyInfo && (
                                <>
                                  {companyInfo.companyName && (
                                    <Col
                                      xs={24}
                                      md={12}
                                      data-id="assignmentCompanyName"
                                    >
                                      {assignmentType ===
                                      AssignmentTypeEnum.SCAssignment ? (
                                        <>
                                          <div className="title">
                                            Parent SC company:
                                          </div>
                                          {isPartner ? (
                                            companyInfo.companyName
                                          ) : (
                                            <div>
                                              <Link
                                                to={`/company/${companyInfo.companyId}`}
                                              >
                                                {companyInfo.companyName}
                                              </Link>
                                            </div>
                                          )}
                                        </>
                                      ) : (
                                        <>
                                          <div className="title">Company:</div>
                                          <div>{companyInfo.companyName}</div>
                                        </>
                                      )}
                                    </Col>
                                  )}

                                  {(companyInfo.representativeContact
                                    ?.firstName ||
                                    companyInfo.representativeContact
                                      ?.lastName) && (
                                    <Col
                                      xs={24}
                                      md={12}
                                      data-id="assignmentContractorPrimaryContact"
                                    >
                                      <div className="title">
                                        Primary contact:
                                      </div>
                                      <div>
                                        {getFullName(
                                          companyInfo.representativeContact
                                            ?.firstName,
                                          companyInfo.representativeContact
                                            ?.lastName,
                                        )}
                                      </div>
                                    </Col>
                                  )}

                                  {companyInfo.representativeContact?.email && (
                                    <Col
                                      xs={24}
                                      md={12}
                                      data-id="assignmentContractorPCEmail"
                                    >
                                      <div className="title">PC email:</div>
                                      <div>
                                        {
                                          companyInfo.representativeContact
                                            ?.email
                                        }
                                      </div>
                                    </Col>
                                  )}

                                  {companyInfo.representativeContact
                                    ?.phoneNumber && (
                                    <Col
                                      xs={24}
                                      md={12}
                                      data-id="assignmentContractorPCPhoneNumber"
                                    >
                                      <div className="title">
                                        PC phone number:
                                      </div>
                                      <div>
                                        {
                                          companyInfo.representativeContact
                                            ?.phoneNumber
                                        }
                                      </div>
                                    </Col>
                                  )}

                                  {assignmentType ===
                                    AssignmentTypeEnum.ICAssignment && (
                                    <>
                                      {!isNil(
                                        workerInfo?.workerInfo?.taxId,
                                      ) && (
                                        <Col
                                          xs={24}
                                          md={12}
                                          data-id="workerTaxId"
                                        >
                                          <div className="title">Tax ID:</div>
                                          <div>
                                            {workerInfo?.workerInfo?.taxId}
                                          </div>
                                        </Col>
                                      )}

                                      {!!workerInfo?.workerFile && (
                                        <Col xs={24} md={12}>
                                          <div className="title">W9:</div>
                                          <span
                                            className="link-imitation"
                                            onClick={handleDownloadWorkerFile}
                                          >
                                            {getFileNameWithoutExt(
                                              workerInfo?.workerFile?.fileName,
                                            )}
                                          </span>
                                        </Col>
                                      )}
                                    </>
                                  )}

                                  {assignmentType ===
                                    AssignmentTypeEnum.SCAssignment && (
                                    <>
                                      {!isNil(
                                        companyInfo?.representativeContact
                                          ?.taxId,
                                      ) && (
                                        <Col
                                          xs={24}
                                          md={12}
                                          data-id="contractorCompanyTaxId"
                                        >
                                          <div className="title">Tax ID:</div>
                                          <div>
                                            {
                                              companyInfo?.representativeContact
                                                ?.taxId
                                            }
                                          </div>
                                        </Col>
                                      )}

                                      {!!companyInfo.workerFile && (
                                        <Col xs={24} md={12}>
                                          <div className="title">W9:</div>
                                          <span
                                            className="link-imitation"
                                            onClick={
                                              handleDownloadContractorCompanyFile
                                            }
                                          >
                                            {getFileNameWithoutExt(
                                              companyInfo.workerFile?.fileName,
                                            )}
                                          </span>
                                        </Col>
                                      )}
                                    </>
                                  )}

                                  {companyAddress && (
                                    <Col
                                      xs={24}
                                      md={12}
                                      data-id="assignmentBusinessAddress"
                                    >
                                      <div className="title">
                                        Business address:
                                      </div>
                                      <div>{companyAddress}</div>
                                    </Col>
                                  )}
                                </>
                              )}

                            <>
                              {(workerInfo?.workerInfo?.firstName ||
                                workerInfo?.workerInfo?.lastName) && (
                                <Col xs={24} md={12} data-id="assignmentWorker">
                                  <div className="title">
                                    Worker:
                                    {workerInfo?.isDuplicated &&
                                      isAdminRole && (
                                        <Tooltip
                                          overlayClassName="light-tooltip"
                                          title="This worker is potentially a duplicate"
                                          trigger="click"
                                          placement="topLeft"
                                          getPopupContainer={(triggerNode) =>
                                            triggerNode.parentNode as HTMLElement
                                          }
                                        >
                                          <span
                                            className={cx(
                                              'icon icon-attention-filled',
                                              styles.tooltipOpener,
                                            )}
                                          />
                                        </Tooltip>
                                      )}
                                  </div>

                                  <div>
                                    {!isPartner && workerInfo?.workerKey ? (
                                      <Link
                                        className="card-title"
                                        to={`/worker/${workerInfo?.workerKey}`}
                                      >
                                        {getFullName(
                                          workerInfo.workerInfo.firstName,
                                          workerInfo.workerInfo.lastName,
                                        )}
                                      </Link>
                                    ) : (
                                      <span>
                                        {getFullName(
                                          workerInfo.workerInfo.firstName,
                                          workerInfo.workerInfo.lastName,
                                        )}
                                      </span>
                                    )}
                                  </div>
                                </Col>
                              )}

                              {workerInfo?.workerInfo?.email && (
                                <Col
                                  xs={24}
                                  md={12}
                                  data-id="assignmentWorkerEmail"
                                >
                                  <div className="title">Worker email:</div>
                                  <div>{workerInfo.workerInfo.email}</div>
                                </Col>
                              )}

                              {workerInfo?.workerInfo?.phoneNumber && (
                                <Col
                                  xs={24}
                                  md={12}
                                  data-id="assignmentWorkerPhoneNumber"
                                >
                                  <div className="title">
                                    Worker phone number:
                                  </div>
                                  <div>{workerInfo.workerInfo.phoneNumber}</div>
                                </Col>
                              )}

                              {workerAddress && (
                                <Col
                                  xs={24}
                                  md={12}
                                  data-id="assignmentHomeAddress"
                                >
                                  <div className="title">
                                    Home address:
                                    {workerInfo?.location
                                      ?.isShowStatePriceAgreementWarning && (
                                      <Tooltip
                                        overlayClassName="light-tooltip"
                                        title="Selected State is not part of current Pricing Agreement. Administrator will reach out with next steps"
                                        trigger="click"
                                        placement="topLeft"
                                        getPopupContainer={(triggerNode) =>
                                          triggerNode.parentNode as HTMLElement
                                        }
                                      >
                                        <span
                                          className={cx(
                                            'icon icon-attention-filled',
                                            styles.tooltipOpener,
                                          )}
                                        />
                                      </Tooltip>
                                    )}
                                  </div>
                                  <div>{workerAddress}</div>
                                </Col>
                              )}
                            </>
                          </Row>
                        </div>
                      </BasePageCard>
                    </Col>
                  )}

                  {isWorksiteInfoVisible && (
                    <Col xs={24} className="d-flex">
                      <BasePageCard
                        title="Worksite location"
                        icons={<span className="icon icon-marker"></span>}
                        className="stretch-card"
                      >
                        <div className="content-wrapper">
                          <Row gutter={[8, 8]} className="titled-sheet">
                            {workAddress && (
                              <Col
                                xs={24}
                                md={12}
                                data-id="assignmentWorkLocation"
                              >
                                <div className="title">Work location:</div>
                                <div>{workAddress}</div>
                              </Col>
                            )}

                            {billingAddress && (
                              <Col
                                xs={24}
                                md={12}
                                data-id="assignmentBillingLocation"
                              >
                                <div className="title">Billing location:</div>
                                <div>{billingAddress}</div>
                              </Col>
                            )}

                            {billingEmail && (
                              <Col
                                xs={24}
                                md={12}
                                data-id="assignmentBillingEmail"
                              >
                                <div className="title">Billing email:</div>
                                <div>{billingEmail}</div>
                              </Col>
                            )}
                          </Row>
                        </div>
                      </BasePageCard>
                    </Col>
                  )}

                  {isAssignmentInfoVisible && (
                    <Col xs={24} className="d-flex">
                      <BasePageCard
                        title="Assignment information"
                        icons={<span className="icon icon-writing-tool"></span>}
                        className="stretch-card"
                      >
                        <div>
                          {!!assignmentTeammates?.length && (
                            <>
                              <div className="content-wrapper">
                                <Row
                                  data-id="assignmentInternalReporting"
                                  className="titled-sheet"
                                >
                                  <Col
                                    xs={24}
                                    data-id="assignmentInternalReporting"
                                  >
                                    <div>
                                      <div className="title">
                                        Internal reporting:
                                      </div>
                                      <div>{joinedTeammateSections()}</div>
                                    </div>
                                  </Col>
                                </Row>
                              </div>
                              <Divider className="no-margin" />
                            </>
                          )}
                          <div className="content-wrapper">
                            <Row gutter={[8, 8]} className="titled-sheet">
                              {assignmentInfo?.jobTitle && (
                                <Col
                                  xs={24}
                                  md={12}
                                  data-id="assignmentJobTitle"
                                >
                                  <div className="title">Job title:</div>
                                  <div>{assignmentInfo?.jobTitle}</div>
                                </Col>
                              )}
                              {!isNil(assignmentInfo?.startDate) && (
                                <Col
                                  xs={24}
                                  md={12}
                                  data-id="assignmentStartDate"
                                >
                                  <div className="title">Start date:</div>
                                  <div>
                                    {moment
                                      .utc(assignmentInfo?.startDate)
                                      .format('MM/DD/YYYY')}
                                  </div>
                                </Col>
                              )}
                              {!isNil(assignmentInfo?.endDate) && (
                                <Col
                                  xs={24}
                                  md={12}
                                  data-id="assignmentEndDate"
                                >
                                  <div className="title">
                                    End date (expected):
                                  </div>
                                  <div>
                                    {moment
                                      .utc(assignmentInfo?.endDate)
                                      .format('MM/DD/YYYY')}
                                  </div>
                                </Col>
                              )}
                              {!isNil(assignmentInfo?.jobCategory) && (
                                <Col
                                  xs={24}
                                  md={12}
                                  data-id="assignmentEmploymentCategory"
                                >
                                  <div className="title">Job category:</div>
                                  <div>
                                    {getEmploymentCategoryLabel(
                                      assignmentInfo?.jobCategory,
                                    )}
                                  </div>
                                </Col>
                              )}
                              {assignmentType ===
                                AssignmentTypeEnum.EEAssignment &&
                                !isNil(assignmentInfo?.employmentType) && (
                                  <Col
                                    xs={24}
                                    md={12}
                                    data-id="assignmentEmploymentType"
                                  >
                                    <div className="title">
                                      Employment type:
                                    </div>
                                    <div>
                                      {getEmploymentTypeLabel(
                                        assignmentInfo?.employmentType,
                                      )}
                                    </div>
                                  </Col>
                                )}
                              {!isNil(
                                assignmentInfo?.anticipatedScheduleType,
                              ) && (
                                <Col
                                  xs={24}
                                  md={12}
                                  data-id="assignmentAnticipatedScheduleType"
                                >
                                  <div className="title">Schedule:</div>
                                  <div>
                                    {getAnticipateScheduleLabel(
                                      assignmentInfo?.anticipatedScheduleType,
                                    )}
                                  </div>
                                </Col>
                              )}
                              {!isNil(assignmentInfo?.workCode) && (
                                <Col
                                  xs={24}
                                  md={12}
                                  data-id="assignmentWorkCode"
                                >
                                  <div className="title">WC Code:</div>
                                  <div>{assignmentInfo.workCode}</div>
                                </Col>
                              )}
                              {assignmentInfo?.customerPurchaseOrder && (
                                <Col xs={24} data-id="customerPurchaseOrder">
                                  <div className="title">
                                    Customer PO Number:
                                  </div>
                                  <div>
                                    {assignmentInfo?.customerPurchaseOrder}
                                  </div>
                                </Col>
                              )}

                              {(assignmentInfo.attachments || [])?.length >
                                0 && (
                                <Col xs={24} data-id="supportingDocuments">
                                  <div className="title">Supporting files:</div>
                                  {assignmentInfo.attachments?.map((file) => (
                                    <div
                                      key={file.fileKey}
                                      className="link-imitation"
                                      onClick={() =>
                                        handleSupportingFileDownload(file)
                                      }
                                    >
                                      {getFileNameWithoutExt(file.fileName)}
                                    </div>
                                  ))}
                                </Col>
                              )}

                              {jobDescriptionContent && (
                                <Col xs={24} data-id="assignmentJobDescription">
                                  <div className="title">Job description:</div>
                                  <BaseTextEditorContent
                                    content={jobDescriptionContent}
                                  />
                                </Col>
                              )}

                              {!isNil(assignmentInfo?.workFromHome) && (
                                <Col
                                  xs={24}
                                  md={12}
                                  data-id="assignmentWorkFromHome"
                                >
                                  <div className="title">Working location:</div>
                                  <div>
                                    {getWorkFromHomeLabel(
                                      assignmentInfo?.workFromHome,
                                    )}
                                  </div>
                                </Col>
                              )}
                              <Col
                                xs={24}
                                md={12}
                                data-id="assignmentPreScreening"
                              >
                                <div className="title">Pre-screen:</div>
                                <div>
                                  {assignmentInfo?.preScreeningRequired
                                    ? 'Required'
                                    : 'Not required'}
                                </div>
                              </Col>
                              {!isNil(assignmentInfo?.preScreeningBilledTo) && (
                                <Col
                                  xs={24}
                                  md={12}
                                  data-id="assignmentPreScreeningBilledTo"
                                >
                                  <div className="title">
                                    Pre-screen billed to:
                                  </div>
                                  <div>
                                    {getPreScreeningBilledToLabel(
                                      assignmentInfo?.preScreeningBilledTo,
                                    )}
                                  </div>
                                </Col>
                              )}
                              {assignmentInfo?.specialInstructions && (
                                <Col
                                  xs={24}
                                  data-id="assignmentSpecialInstructions"
                                >
                                  <div className="title">
                                    Special instructions:
                                  </div>
                                  <div>
                                    {assignmentInfo?.specialInstructions}
                                  </div>
                                </Col>
                              )}
                            </Row>
                          </div>
                        </div>
                      </BasePageCard>
                    </Col>
                  )}
                </Row>
              </Col>
            )}

            {(!isNil(customerInfo) ||
              isSupervisorInfoVisible ||
              isTimesheetApproverVisible ||
              isTimesheetBackupApproverVisible ||
              isPrimaryContactInfoVisible) && (
              <Col xs={24} md={12} className="d-flex">
                <BasePageCard
                  className="stretch-card"
                  title={
                    isCorporateAssignment
                      ? 'Contact information'
                      : 'Customer information'
                  }
                  icons={<span className="icon icon-info"></span>}
                >
                  {!isCorporateAssignment && (
                    <>
                      <div className="content-wrapper">
                        <Row gutter={[8, 8]} className="titled-sheet">
                          <Col xs={24} data-id="assignmentGeneralInfo">
                            <div className="subtitle">General information</div>
                          </Col>

                          {customerInfo?.companyName && (
                            <Col xs={24} md={12} data-id="assignmentCustomer">
                              <div className="title">Customer:</div>
                              <div>
                                {isPartner ? (
                                  customerInfo?.companyName
                                ) : (
                                  <Link
                                    to={`/customer/${customerInfo?.customerKey}`}
                                  >
                                    {customerInfo?.companyName}
                                  </Link>
                                )}
                              </div>
                            </Col>
                          )}
                        </Row>
                      </div>
                      <Divider className="no-margin" />
                    </>
                  )}

                  {isPrimaryContactInfoVisible && (
                    <>
                      <Row gutter={[8, 8]} className="titled-sheet">
                        <Col xs={24}>
                          <div className="subtitle subtitle-indent">
                            Primary contact information
                          </div>
                        </Col>
                        <Col xs={24}>
                          <div className="scroll-box scroll-box-lg">
                            {bestPrimaryContacts.map(
                              (
                                contact: AssignmentContactPersonViewModel,
                                index,
                              ) => (
                                <PrimaryContactInfoSection
                                  index={index}
                                  contact={contact}
                                  key={`assignment-primary-contact-info-${index}`}
                                />
                              ),
                            )}
                          </div>
                        </Col>
                      </Row>
                      <Divider className="no-margin" />
                    </>
                  )}

                  {isBillingContactsInfoVisible && (
                    <>
                      <Row gutter={[8, 8]} className="titled-sheet">
                        <Col xs={24}>
                          <div className="subtitle subtitle-indent">
                            Billing contact information
                          </div>
                        </Col>
                        <Col xs={24}>
                          <div className="scroll-box scroll-box-lg">
                            {bestBillingContacts.map(
                              (
                                contact: AssignmentContactPersonViewModel,
                                index,
                              ) => (
                                <BillingContactInfoSection
                                  index={index}
                                  contact={contact}
                                  key={`assignment-billing-contact-info-${index}`}
                                />
                              ),
                            )}
                          </div>
                        </Col>
                      </Row>
                      <Divider className="no-margin" />
                    </>
                  )}

                  {isSupervisorInfoVisible && (
                    <>
                      <div className="content-wrapper">
                        <Row gutter={[8, 8]} className="titled-sheet">
                          <Col xs={24}>
                            <div className="subtitle">
                              Supervisor information
                            </div>
                          </Col>

                          {supervisorFullName && (
                            <Col xs={24} md={12} data-id="assignmentSupervisor">
                              <div className="title">Supervisor:</div>
                              <div>{supervisorFullName}</div>
                            </Col>
                          )}

                          {supervisorInfo?.title && (
                            <Col
                              xs={24}
                              md={12}
                              data-id="assignmentSupervisorTitle"
                            >
                              <div className="title">Title:</div>
                              <div>{supervisorInfo.title}</div>
                            </Col>
                          )}

                          {supervisorInfo?.email && (
                            <Col
                              xs={24}
                              md={12}
                              data-id="assignmentSupervisorEmail"
                            >
                              <div className="title">Email:</div>
                              <div>{supervisorInfo.email}</div>
                            </Col>
                          )}

                          {supervisorInfo?.phoneNumber && (
                            <Col
                              xs={24}
                              md={12}
                              data-id="assignmentSupervisorPhoneNumber"
                            >
                              <div className="title">Phone number:</div>
                              <div>{supervisorInfo.phoneNumber}</div>
                            </Col>
                          )}
                        </Row>
                      </div>

                      <Divider className="no-margin" />
                    </>
                  )}

                  {isTimesheetApproverVisible && (
                    <>
                      <Row gutter={[8, 8]} className="titled-sheet">
                        <Col xs={24}>
                          <div className="subtitle subtitle-indent">
                            Timesheet approver information
                          </div>
                        </Col>

                        {timesheetApproverNotApplicable && (
                          <Col
                            xs={24}
                            md={12}
                            data-id="assignmentTimesheetApprover"
                          >
                            <div className="divider-box sub-content">
                              <div className="content-wrapper">
                                <div className="title">Timesheet approver:</div>
                                <div>Not applicable</div>
                              </div>
                            </div>
                          </Col>
                        )}

                        {!timesheetApproverNotApplicable &&
                          !!timesheetApprovers.length && (
                            <Col xs={24}>
                              <div className="scroll-box scroll-box-lg">
                                {timesheetApprovers.map(
                                  (timesheetApprover, index) => (
                                    <div
                                      className="divider-box sub-content"
                                      key={`${timesheetApprover.id}-${index}`}
                                    >
                                      <div className="content-wrapper">
                                        <Row gutter={[8, 8]}>
                                          {getFullName(
                                            timesheetApprover?.firstName,
                                            timesheetApprover?.lastName,
                                          ) && (
                                            <Col
                                              xs={24}
                                              md={12}
                                              data-id={`assignmentTimesheetApproverFullName-${index}`}
                                            >
                                              <div className="title">
                                                TS approver:
                                              </div>
                                              <div>
                                                {getFullName(
                                                  timesheetApprover?.firstName,
                                                  timesheetApprover?.lastName,
                                                )}
                                              </div>
                                            </Col>
                                          )}
                                          {timesheetApprover?.email && (
                                            <Col
                                              xs={24}
                                              md={12}
                                              data-id={`assignmentTimesheetApproverEmail-${index}`}
                                            >
                                              <div className="title">
                                                Email:
                                              </div>
                                              <div>
                                                {timesheetApprover?.email}
                                              </div>
                                            </Col>
                                          )}
                                          {timesheetApprover?.phoneNumber && (
                                            <Col
                                              xs={24}
                                              md={12}
                                              data-id={`assignmentTimesheetApproverPhoneNumber-${index}`}
                                            >
                                              <div className="title">
                                                Phone number:
                                              </div>
                                              <div>
                                                {timesheetApprover.phoneNumber}
                                              </div>
                                            </Col>
                                          )}
                                        </Row>
                                      </div>
                                      <Divider className="card-divider no-margin" />
                                    </div>
                                  ),
                                )}
                              </div>
                            </Col>
                          )}
                      </Row>
                    </>
                  )}

                  {isTimesheetBackupApproverVisible && (
                    <>
                      <Divider className="no-margin" />
                      <div className="content-wrapper">
                        <Row gutter={[8, 8]} className="titled-sheet">
                          <Col xs={24}>
                            <div className="subtitle">
                              Timesheet backup approver information
                            </div>
                          </Col>

                          {backupApproverFullName && (
                            <Col
                              xs={24}
                              md={12}
                              data-id="assignmentBackupApproverFullName"
                            >
                              <div className="title">TS approver:</div>
                              <div>{backupApproverFullName}</div>
                            </Col>
                          )}

                          {timesheetBackupApproverInfo?.email && (
                            <Col
                              xs={24}
                              md={12}
                              data-id="assignmentBackupApproverEmail"
                            >
                              <div className="title">Email:</div>
                              <div>{timesheetBackupApproverInfo?.email}</div>
                            </Col>
                          )}

                          {timesheetBackupApproverInfo?.phoneNumber && (
                            <Col
                              xs={24}
                              md={12}
                              data-id="assignmentBackupApproverPhoneNumber"
                            >
                              <div className="title">Phone number:</div>
                              <div>
                                {timesheetBackupApproverInfo.phoneNumber}
                              </div>
                            </Col>
                          )}
                        </Row>
                      </div>
                    </>
                  )}
                </BasePageCard>
              </Col>
            )}
            {areObjectValuesPresent(resultFinancialInfo) && (
              <FinancialInfoDetails
                assignmentDetails={assignmentDetails}
                financialInfoSchedulerInitialValues={financialScheduler}
                onSuccess={loadAssignmentDetails}
              />
            )}
            <Col xs={24} md={12} className="d-flex">
              <BasePageCard
                className="stretch-card"
                title="Split Information"
                icons={<span className="icon icon-split"></span>}
              >
                <div className="content-wrapper">
                  <Row gutter={[8, 8]} className="titled-sheet">
                    <Col xs={24} md={12} data-id="assignmentSplitType">
                      <div className="title">Split:</div>
                      <div>{getSplitTypeLabel(splitInfo?.splitType)}</div>
                    </Col>

                    {partnerInfo?.companyName && (
                      <Col xs={24} md={12} data-id="assignmentSplitCompanyName">
                        <div className="title">Member split company:</div>
                        <div>{partnerInfo.companyName}</div>
                      </Col>
                    )}

                    {(partnerInfo?.firstName || partnerInfo?.lastName) && (
                      <Col
                        xs={24}
                        md={12}
                        data-id="assignmentSplitPrimaryContact"
                      >
                        <div className="title">Primary contact:</div>
                        <div>
                          {getFullName(
                            partnerInfo?.firstName,
                            partnerInfo?.lastName,
                          )}
                        </div>
                      </Col>
                    )}

                    {partnerInfo?.email && (
                      <Col xs={24} md={12} data-id="assignmentSplitPCEmail">
                        <div className="title">PC email:</div>
                        <div>{partnerInfo.email}</div>
                      </Col>
                    )}

                    {partnerInfo?.phoneNumber && (
                      <Col
                        xs={24}
                        md={12}
                        data-id="assignmentSplitPCPhoneNumber"
                      >
                        <div className="title">PC phone number:</div>
                        <div>{partnerInfo?.phoneNumber}</div>
                      </Col>
                    )}

                    {(!isNil(splitInfo?.splitAmount) ||
                      !isNil(splitInfo?.splitPercent)) && (
                      <Col xs={24} md={12} data-id="assignmentSplitAmount">
                        <div className="title">Split amount:</div>
                        <div>
                          Profit Split:{' '}
                          {getPartnerProfitSplit(
                            splitInfo?.splitPercent,
                            splitInfo?.splitAmount,
                          )}
                        </div>
                      </Col>
                    )}
                  </Row>
                </div>
              </BasePageCard>
            </Col>
          </Row>
        </div>
      </div>
    </>
  );
}
